import React from 'react';

import { StatusBadge } from './StatusBadge';

const DISPLAY_STATUSES = new Set(['pending', 'running', 'completed', 'failed']);

function getDisplayStatus(planItem) {
  const durableStatus = String(
    planItem?.status || planItem?.latest_attempt?.status || ''
  ).toLowerCase();
  return durableStatus === 'planned' ? 'pending' : durableStatus;
}

function getFailureMessage(planItem) {
  const failure = planItem?.last_error || planItem?.latest_attempt?.failure_summary;
  if (!failure) return null;
  if (typeof failure === 'string') return failure;
  return failure.message || failure.detail || null;
}

function getRepresentedRunIds(values) {
  return new Set(
    Array.from(values || [], (value) =>
      String(value?.id ?? value)
    )
  );
}

function defaultSampleLabel(sampleId) {
  return sampleId == null ? 'Unassigned sample' : `Sample ${sampleId}`;
}

function defaultInputLabel(inputId) {
  return `Input ${inputId}`;
}

export function ActiveExecutionRequestPanel({
  executionRequest = null,
  planItems = null,
  representedWorkflowRunIds = [],
  getSampleLabel = defaultSampleLabel,
  getInputLabel = defaultInputLabel,
  onOpenRun = null,
}) {
  const sourcePlanItems = Array.isArray(planItems)
    ? planItems
    : Array.isArray(executionRequest?.plan_items)
      ? executionRequest.plan_items
      : [];
  const representedRunIds = getRepresentedRunIds(representedWorkflowRunIds);
  const visiblePlanItems = sourcePlanItems
    .map((planItem) => ({
      planItem,
      displayStatus: getDisplayStatus(planItem),
    }))
    .filter(({ planItem, displayStatus }) => {
      if (!DISPLAY_STATUSES.has(displayStatus)) return false;
      const workflowRunId = planItem?.workflow_run_id;
      return !workflowRunId || !representedRunIds.has(String(workflowRunId));
    });

  if (!visiblePlanItems.length) return null;

  return (
    <section
      aria-label="Active analyses"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Currently processing
          </h3>
          <p className="text-xs text-slate-500">
            {visiblePlanItems.length}{' '}
            {visiblePlanItems.length === 1 ? 'analysis' : 'analyses'} awaiting the
            saved-runs list.
          </p>
        </div>
      </div>

      <ul className="mt-3 max-h-72 divide-y divide-slate-100 overflow-y-auto rounded border border-slate-200">
        {visiblePlanItems.map(({ planItem, displayStatus }) => {
          const sampleLabel = getSampleLabel(planItem.sample_id);
          const inputLabels = (planItem.input_import_ids || []).map(getInputLabel);
          const workflowRunId = planItem.workflow_run_id || null;
          const failureMessage = getFailureMessage(planItem);

          return (
            <li
              key={planItem.plan_item_id}
              data-testid={`active-plan-item-${planItem.plan_item_id}`}
              className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">{sampleLabel}</span>
                  <span className="text-xs uppercase tracking-wide text-slate-500">
                    {planItem.pipeline || 'Analysis'}
                  </span>
                </div>
                {inputLabels.length > 0 && (
                  <p className="truncate text-xs text-slate-500">
                    {inputLabels.join(', ')}
                  </p>
                )}
                {displayStatus === 'failed' && failureMessage && (
                  <p className="mt-1 text-xs text-rose-700">{failureMessage}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <StatusBadge status={displayStatus} />
                {onOpenRun && (
                  <button
                    type="button"
                    disabled={!workflowRunId}
                    onClick={() => onOpenRun(workflowRunId)}
                    className="rounded border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Open
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
